import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type RevertAllTemplateCustomizationsMutation,
  type RevertAllTemplateCustomizationsMutationVariables,
} from 'graphql-query-contracts';
import { revertAllTemplateCustomizationsMutation } from 'graphql-queries';

import { RevertAllTemplateCustomizationsError } from './errors/RevertAllTemplateCustomizationsError';

export type RevertAllTemplateCustomizationsParams = {
  channelId: string;
  templateId: string;
};

export type RevertAllTemplateCustomizationsResult = {
  success: boolean;
  newVersion: number;
};

export type UseRevertAllTemplateCustomizationsResult = {
  revertAllTemplateCustomizations: (
    params: RevertAllTemplateCustomizationsParams
  ) => Promise<RevertAllTemplateCustomizationsResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: RevertAllTemplateCustomizationsParams): void {
  if (!params.channelId?.trim()) {
    throw new RevertAllTemplateCustomizationsError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new RevertAllTemplateCustomizationsError('templateId is required');
  }
}

export const useRevertAllTemplateCustomizations =
  (): UseRevertAllTemplateCustomizationsResult => {
    const [revertAll, { loading }] = useMutation<
      RevertAllTemplateCustomizationsMutation,
      RevertAllTemplateCustomizationsMutationVariables
    >(revertAllTemplateCustomizationsMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const revertAllTemplateCustomizations = useCallback(
      async (
        params: RevertAllTemplateCustomizationsParams
      ): Promise<RevertAllTemplateCustomizationsResult> => {
        try {
          setError(undefined);

          validateParams(params);

          const variables: RevertAllTemplateCustomizationsMutationVariables = {
            input: {
              channelId: params.channelId,
              templateId: params.templateId,
            },
          };

          const mutationResult = await revertAll({ variables });

          const payload =
            mutationResult.data?.revertAllTemplateCustomizations;

          if (!payload) {
            throw new RevertAllTemplateCustomizationsError();
          }

          if (!payload.success) {
            throw new RevertAllTemplateCustomizationsError(
              'Revert all operation did not succeed'
            );
          }

          return {
            success: payload.success,
            newVersion: payload.newVersion,
          };
        } catch (err) {
          const wrappedError =
            err instanceof RevertAllTemplateCustomizationsError
              ? err
              : new RevertAllTemplateCustomizationsError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to revert all inspection template customizations'
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [revertAll]
    );

    return {
      revertAllTemplateCustomizations,
      loading,
      error,
    };
  };
